import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import type { HttpRequestConfig, HttpResponse, IHttpClient } from "@/core/interfaces";

import { getApiBaseUrl } from "@/core/environment";
import { AUTH_TOKEN_COOKIE } from "@/core/constants/keys";
import { Paths } from "@/core/constants/paths";
import { getCookie } from "@/core/utils/cookie";
import { logError, logWarning, NetworkErrorException, APIErrorException } from "@/core/services";
import { clearAuthToken } from "@/modules/auth/auth.service";

function mapConfig(config?: HttpRequestConfig): AxiosRequestConfig {
  return { headers: config?.headers, params: config?.params, signal: config?.signal };
}

function redirectOnSessionExpired(): void {
  window.location.replace(`${window.location.origin}${Paths.LOGIN}`);
}

export class AxiosHttpClient implements IHttpClient {
  private readonly instance: AxiosInstance;

  constructor(baseURL = getApiBaseUrl()) {
    this.instance = axios.create({
      baseURL,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
    });

    this.instance.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
      const token = getCookie(AUTH_TOKEN_COOKIE);
      if (token && cfg.headers) {
        cfg.headers.Authorization = `Bearer ${token}`;
      }
      return cfg;
    });

    this.instance.interceptors.response.use(
      (res) => res,
      (error) => {
        if (!axios.isAxiosError(error)) {
          logError(error, { component: "HttpClient" });
          return Promise.reject(error);
        }

        const status = error.response?.status;

        // 403 não desloga: segue como APIErrorException para a tela exibir "sem permissão".
        if (status === 401) {
          logWarning("Sessão expirada (401), redirecionando para login", {
            url: error.config?.url,
          });
          clearAuthToken();
          redirectOnSessionExpired();
          return new Promise(() => {});
        }

        if (!error.response) {
          const networkErr = new NetworkErrorException("Erro de conexão com o servidor");
          logError(networkErr, { component: "HttpClient", action: error.config?.url });
          return Promise.reject(networkErr);
        }

        const apiErr = new APIErrorException(
          status ?? 0,
          error.response.data?.message ?? error.message,
          JSON.stringify(error.response.data),
        );
        logError(apiErr, { component: "HttpClient", action: error.config?.url });
        return Promise.reject(apiErr);
      },
    );
  }

  async get<T>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    const res = await this.instance.get<T>(url, mapConfig(config));
    return { data: res.data, status: res.status };
  }

  async post<T>(url: string, body?: unknown, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    const res = await this.instance.post<T>(url, body, mapConfig(config));
    return { data: res.data, status: res.status };
  }

  async put<T>(url: string, body?: unknown, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    const res = await this.instance.put<T>(url, body, mapConfig(config));
    return { data: res.data, status: res.status };
  }

  async patch<T>(
    url: string,
    body?: unknown,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>> {
    const res = await this.instance.patch<T>(url, body, mapConfig(config));
    return { data: res.data, status: res.status };
  }

  async delete<T>(url: string, config?: HttpRequestConfig): Promise<HttpResponse<T>> {
    const res = await this.instance.delete<T>(url, mapConfig(config));
    return { data: res.data, status: res.status };
  }
}
